import { normalizeHexFlexible } from './productColorDisplay';

/**
 * Canonical stock size keys — must match `formatSizeForStockApi` in services/api.js and DB.
 * Do not use.toUpperCase() for all sizes: `default` must stay lowercase.
 */
export function normalizeStockSizeKey(size) {
  const s = String(size ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return s;
  const lower = s.toLowerCase();
  if (lower === 'default') return 'default';
  if (lower === 'default size') return 'Default size';
  // Single canonical key for EU/US shoe labels, kids 2T, etc. (must match API + bulk stock)
  return s.toUpperCase();
}

/**
 * Color string as sent on stock APIs — preserves DB casing (esp. hex) so bulk merges the same bucket.
 * Only normalizes the placeholder `default` alias.
 */
export function stockColorKeyForApi(color) {
  const c = String(color ?? '').trim();
  if (!c) return c;
  if (c.toLowerCase() === 'default') return 'default';
  return c;
}

/**
 * Size string as sent on stock APIs — trim + whitespace collapse + default aliases; no forced uppercase
 * so keys stay aligned with how they were stored when the backend is case-sensitive.
 */
export function stockSizeKeyForApi(size) {
  const s = String(size ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return s;
  const lower = s.toLowerCase();
  if (lower === 'default') return 'default';
  if (lower === 'default size') return 'Default size';
  return s;
}

/** Canonical placeholder color key (lowercase `default`). Hex labels → uppercase #RRGGBB. */
export function normalizeStockColorKey(color) {
  const c = String(color ?? '').trim();
  if (!c) return c;
  if (c.toLowerCase() === 'default') return 'default';
  const hex = normalizeHexFlexible(c);
  if (hex) return hex;
  return c;
}

/** True when two color labels refer to the same variant (hex-aware, case-insensitive names). */
export function colorsStockMatch(a, b) {
  const ca = String(a ?? '').trim();
  const cb = String(b ?? '').trim();
  if (!ca || !cb) return ca === cb;
  if (ca.toLowerCase() === 'default' && cb.toLowerCase() === 'default') return true;
  const ha = normalizeHexFlexible(ca);
  const hb = normalizeHexFlexible(cb);
  if (ha && hb) return ha === hb;
  if (ha || hb) return false;
  return ca.toLowerCase() === cb.toLowerCase();
}

/**
 * Prefer an existing `stock` object key so bulk updates merge with the DB instead of orphan keys.
 */
export function resolveStockColorKey(label, stock) {
  const base = normalizeStockColorKey(label);
  if (!stock || typeof stock !== 'object') return base;
  for (const ck of Object.keys(stock)) {
    if (colorsStockMatch(ck, label) || colorsStockMatch(ck, base)) {
      return ck;
    }
  }
  return base;
}

export function dedupeStockColorList(colors) {
  if (!Array.isArray(colors)) return [];
  const out = [];
  for (const c of colors) {
    const t = String(c ?? '').trim();
    if (!t) continue;
    if (!out.some((d) => colorsStockMatch(d, t))) out.push(t);
  }
  return out;
}

/**
 * Read qty from nested stock; tolerates legacy casing on size keys from API.
 */
export function readNestedStockQuantity(stock, colorKey, sizeKey) {
  if (!stock || typeof stock !== 'object') return 0;
  const nk = normalizeStockColorKey(colorKey);
  const ns = normalizeStockSizeKey(sizeKey);
  let bucket = stock[nk] ?? stock[colorKey];
  if (!bucket || typeof bucket !== 'object') {
    for (const ck of Object.keys(stock)) {
      if (colorsStockMatch(ck, nk) || colorsStockMatch(ck, colorKey)) {
        bucket = stock[ck];
        break;
      }
    }
  }
  if (!bucket || typeof bucket !== 'object') return 0;
  if (bucket[ns] !== undefined && bucket[ns] !== '') {
    const n = parseInt(bucket[ns], 10);
    return Number.isNaN(n) ? 0 : n;
  }
  for (const k of Object.keys(bucket)) {
    if (normalizeStockSizeKey(k) === ns) {
      const n = parseInt(bucket[k], 10);
      return Number.isNaN(n) ? 0 : n;
    }
  }
  return 0;
}
